import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Droplets, Zap, Leaf, ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    id: "biofuel",
    icon: Droplets,
    overline: "01 · Bio Fuel Solutions",
    title: "Biofuels",
    desc: "Sustainable energy crafted from agricultural feedstock and waste — engineered to power transport, industry and homes without compromise.",
    bullets: ["Ethanol-Blend", "Biodiesel", "Bio CNG", "Renewable Drop-in Fuels"],
    img: "/generated/bio_fuel.png",
    fallback: "https://images.unsplash.com/photo-1615209853186-e4bd66602508?auto=format&fit=crop&w=1400&q=80",
    accent: "#138808",
  },
  {
    id: "ev",
    icon: Zap,
    overline: "02 · EV Mobility",
    title: "EV Charging Infrastructure",
    desc: "A pan-India network of intelligent fast-charging stations and smart mobility hubs — built for fleets, commuters and the highway of tomorrow.",
    bullets: ["DC Fast Charging (60–240kW)", "Smart Grid Integration", "Highway Corridor Network", "Fleet Charging Hubs"],
    img: "/generated/ev_charging.png",
    fallback: "https://images.unsplash.com/photo-1760538978585-f82dc257ec15?auto=format&fit=crop&w=1400&q=80",
    accent: "#FF9933",
  },
  {
    id: "green",
    icon: Leaf,
    overline: "03 · Green Energy",
    title: "Industrial Green Solutions",
    desc: "End-to-end renewable energy systems for manufacturing, agriculture and infrastructure — converting India's industrial backbone to clean power.",
    bullets: ["Solar + Storage Plants", "Waste-to-Energy Systems", "Carbon Offset Programs"],
    img: "/generated/sustainability.png",
    fallback: "https://images.pexels.com/photos/33508509/pexels-photo-33508509.jpeg?auto=compress&cs=tinysrgb&w=1400",
    accent: "#138808",
  },
];

export default function Services() {
  const [active, setActive] = useState(0);
  const navigate = useNavigate();

  function goToSection(id) {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    try {
      navigate("/");
    } catch (e) {
      try {
        window.location.hash = "#/";
      } catch (err) {}
    }

    setTimeout(() => {
      const el2 = document.getElementById(id);
      if (el2) el2.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
  }

  const safeIndex = active >= 0 && active < SERVICES.length ? active : 0;
  const card = SERVICES[safeIndex];

  return (
    <section
      id="services"
      className="relative py-28 md:py-36 bg-ink-surface overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-30"
        style={{
          background:
            "radial-gradient(ellipse at top, rgba(11,122,59,0.25), transparent 60%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <div className="max-w-3xl">
          <span className="overline">What We Are Projecting</span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mt-5 leading-[1.05] tracking-tight">
            Three pillars powering
            <br /> India&apos;s{" "}
            <span className="gradient-text-eco">energy transition</span>.
          </h2>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-8">

          {/* LEFT TABS */}
          <div className="lg:col-span-5 space-y-4">
            {SERVICES.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setActive(i)}
                className={`w-full text-left glass-card p-6 transition-all duration-300 relative ${
                  safeIndex === i
                    ? "border-eco-light/60 shadow-[0_0_50px_rgba(19,136,8,0.25)] scale-[1.01]"
                    : "opacity-80 hover:opacity-100"
                }`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                    style={{
                      background: `${s.accent}22`,
                      border: `1px solid ${s.accent}55`,
                    }}
                  >
                    <s.icon className="w-5 h-5" style={{ color: s.accent }} />
                  </div>

                  <div className="flex-1">
                    <div className="text-[10px] tracking-widest text-white/40 uppercase">
                      {s.overline}
                    </div>
                    <div className="text-xl md:text-2xl font-semibold mt-1">
                      {s.title}
                    </div>
                    <div className="text-sm text-white/55 mt-2 line-clamp-2">
                      {s.desc}
                    </div>
                  </div>

                  <ArrowUpRight
                    className={`w-5 h-5 mt-1 transition-all duration-300 ${
                      safeIndex === i
                        ? "text-eco-light rotate-0"
                        : "text-white/30 -rotate-45"
                    }`}
                  />
                </div>
              </button>
            ))}
          </div>

          {/* RIGHT SHOWCASE */}
          <div className="lg:col-span-7">
            <div
              key={card.id}
              className="glass-card overflow-hidden relative grain transition-all duration-500"
              style={{ minHeight: 600 }}
            >
              {/* IMAGE FIXED (NO CROPPING) */}
              <div className="relative h-72 md:h-96 overflow-hidden bg-black/40 flex items-center justify-center">
                <img
                  src={card.img}
                  onError={(e) => (e.currentTarget.src = card.fallback)}
                  alt={card.title}
                  className="w-full h-full object-contain transition-transform duration-[1.2s]"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-ink-surface via-ink-surface/20 to-transparent" />

                <div className="absolute top-5 left-5">
                  <span
                    className="text-[10px] font-mono-eco tracking-widest uppercase px-3 py-1.5 rounded-full"
                    style={{
                      background: `${card.accent}22`,
                      color: card.accent,
                      border: `1px solid ${card.accent}55`,
                    }}
                  >
                    ● Live Solution
                  </span>
                </div>
              </div>

              {/* CONTENT */}
              <div className="p-7 md:p-10">
                <span className="text-[11px] tracking-widest text-eco-light uppercase">
                  {card.overline}
                </span>

                <h3 className="text-3xl md:text-4xl font-bold mt-2">
                  {card.title}
                </h3>

                <p className="text-white/65 mt-4 leading-relaxed">
                  {card.desc}
                </p>

                <ul className="mt-7 grid sm:grid-cols-2 gap-3">
                  {card.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-center gap-3 text-sm text-white/80"
                    >
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{
                          background: card.accent,
                          boxShadow: `0 0 10px ${card.accent}`,
                        }}
                      />
                      {b}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => goToSection("contact")}
                  className="mt-9 inline-flex items-center gap-2 text-sm font-semibold text-white border-b border-eco-light/60 pb-1 hover:text-eco-light transition-colors"
                >
                  Discuss this solution{" "}
                  <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}